import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

// The reduced 30-day fare starts at midnight in Norway on 1 September 2026.
const useThirtyDayTicketExample = Date.now() >= Date.parse('2026-09-01T00:00:00+02:00');
const busTicket = useThirtyDayTicketExample
    ? {label: '30-dagersbillett', price: 649, quantityMultiplier: 6}
    : {label: '180-dagers periodebillett', price: 4795, quantityMultiplier: 1};

export default new Vuex.Store({
    state: {
        selectedPath: '/',
        selectedIndex: 0,
        comparisons: [
            {
                id: 'busCards',
                image: '/items/bus.svg',
                text: `#value# stk. ${busTicket.label}`,
                info: `${busTicket.price},- pr stk (voksen, 1 sone)`
            },
            {
                id: 'electricBikes',
                image: '/items/electric-bike-brand.svg',
                text: '#value# stk. elsykler',
                info: '30 000,- pr sykkel'
            },
            {
                id: 'bikes',
                image: '/items/bike-brand.svg',
                text: '#value# stk. sykler',
                info: '8000,- pr sykkel'
            },
            {
                id: 'taxiRides',
                image: '/items/taxi-brand.svg',
                text: '#value# taxiturer',
                info: '305,- per tur'
            },
            {
                id: 'daysRentalCar',
                image: '/items/rental-car-brand.svg',
                text: '#value# dager leiebil',
                info: '2090,- per dag'
            },
            {
                id: 'joggingShoes',
                image: '/items/shoes-brand.svg',
                text: '#value# par joggesko',
                info: '1000,- per par'
            },
            {
                id: 'kickbikeRounds',
                image: '/items/kickbike-brand.svg',
                text: '#value# turer el-sparkesykkel',
                info: '40,- per tur a 10 min'
            },
            /* Hide for now cause they wanted 0 of these atm
            {
                id: 'numRentMonths',
                image: '/items/long-term-rental-car-brand.svg',
                text: '#value# måneder bilabonnement',
                info: '6200,- per måned'
            }
            */
        ],
        priceInfo: {
            'busCards': {
                price: busTicket.price,
                unit: `stk. ${busTicket.label}`
            },
            'electricBikes': {
                price: 30000,
                unit: 'stk. elsykler'
            },
            'bikes': {
                price: 8000,
                unit: 'stk. sykler'
            },
            'taxiRides': {
                price: 305,
                unit: 'stk. taxiturer'
            },
            'daysRentalCar': {
                price: 2090,
                unit: 'dager'
            },
            'joggingShoes': {
                price: 1000,
                unit: 'par joggesko'
            },
            'kickbikeRounds': {
                price: 40,
                unit: 'kicksykkel rundt'
            }
        },
        selections: [
            {
                label: 'Bil, gjennomsnitt',
                title: 'Kostnader ved å eie “gjennomsnittsbilen” WV Passat',
                type: 'vanlig bil',
                price: 461000,
                compareValues: {
                    busCards: 4 * busTicket.quantityMultiplier,
                    electricBikes: 1,
                    bikes: 2,
                    taxiRides: 20,
                    daysRentalCar: 10,
                    joggingShoes: 2,
                    kickbikeRounds: 60,
                    numRentMonths: 0
                },
                costs: {
                    writeOffs: 42290,
                    interest: 27755,
                    insurance: 9206,
                    trafficInsuranceFee: 2774,
                    maintenance: 5952,
                    fuel: 15546,
                    oil: 1668,
                    tires: 3618,
                    serviceRepairs: 7587,
                    tolls: 13728
                },
                total: 130123.36,
                video: '/videos/bil_2.mp4',
                fallback: '/videos/bil_2.png',
                idleTime: 4
            },
            {
                label: 'Bil, liten',
                title: 'Kostnader ved å eie en liten bil',
                type: 'liten bil',
                price: 186700,
                compareValues: {
                    busCards: 2 * busTicket.quantityMultiplier,
                    electricBikes: 1,
                    bikes: 1,
                    taxiRides: 10,
                    daysRentalCar: 5,
                    joggingShoes: 1,
                    kickbikeRounds: 20,
                    numRentMonths: 0
                },
                costs: {
                    writeOffs: 27339,
                    interest: 18054,
                    insurance: 7582,
                    trafficInsuranceFee: 2774,
                    maintenance: 5603,
                    fuel: 12487,
                    oil: 1480,
                    tires: 3037,
                    serviceRepairs: 6813,
                    tolls: 13728
                },
                total: 98897.55,
                video: '/videos/bil_1.mp4',
                fallback: '/videos/bil_1.png',
                idleTime: 4
            },
            {
                label: 'Bil, stor/SUV',
                title: 'Kostnader ved å eie en SUV/stor bil',
                type: 'stor bil/SUV',
                price: 750000,
                compareValues: {
                    busCards: 4 * busTicket.quantityMultiplier,
                    electricBikes: 2,
                    bikes: 2,
                    taxiRides: 30,
                    daysRentalCar: 10,
                    joggingShoes: 4,
                    kickbikeRounds: 100,
                    numRentMonths: 0
                },
                costs: {
                    writeOffs: 76764,
                    interest: 50120,
                    insurance: 12601,
                    trafficInsuranceFee: 2774,
                    maintenance: 6723,
                    fuel: 19878,
                    oil: 1892,
                    tires: 4990,
                    serviceRepairs: 8687,
                    tolls: 13728
                },
                total: 198155.68,
                video: '/videos/bil_3.mp4',
                fallback: '/videos/bil_3.png',
                idleTime: 4
            },
            {
                label: 'Elbil, gjennomsnitt',
                type: 'vanlig elbil',
                title: 'Kostnader ved å eie en elbil',
                price: 292400,
                compareValues: {
                    busCards: 2 * busTicket.quantityMultiplier,
                    electricBikes: 1,
                    bikes: 1,
                    taxiRides: 10,
                    daysRentalCar: 5,
                    joggingShoes: 2,
                    kickbikeRounds: 20,
                    numRentMonths: 0
                },
                costs: {
                    writeOffs: 40946,
                    interest: 28310,
                    insurance: 9390,
                    trafficInsuranceFee: 3176,
                    maintenance: 5952,
                    fuel: 2702,
                    oil: 0,
                    tires: 3980,
                    serviceRepairs: 4742,
                    tolls: 9610
                },
                total: 108807.98,
                video: '/videos/bil_5.mp4',
                fallback: '/videos/bil_5.png',
                idleTime: 4
            },
            {
                label: 'Elbil, liten',
                title: 'Kostnader ved å eie en liten elbil',
                type: 'liten elbil',
                price: 186700,
                compareValues: {
                    busCards: busTicket.quantityMultiplier,
                    electricBikes: 1,
                    bikes: 1,
                    taxiRides: 5,
                    daysRentalCar: 1,
                    joggingShoes: 1,
                    kickbikeRounds: 20,
                    numRentMonths: 0
                },
                costs: {
                    writeOffs: 26534,
                    interest: 18415,
                    insurance: 7734,
                    trafficInsuranceFee: 3176,
                    maintenance: 5603,
                    fuel: 2702,
                    oil: 0,
                    tires: 3037,
                    serviceRepairs: 4258,
                    tolls: 9610
                },
                total: 81069.10,
                video: '/videos/bil_4.mp4',
                fallback: '/videos/bil_4.png',
                idleTime: 4
            },
            {
                label: 'Elbil, stor/SUV',
                title: 'Kostnader ved å eie en stor elbil/SUV',
                type: 'stor elbil/SUV',
                price: 755500,
                compareValues: {
                    busCards: 4 * busTicket.quantityMultiplier,
                    electricBikes: 2,
                    bikes: 2,
                    taxiRides: 20,
                    daysRentalCar: 10,
                    joggingShoes: 4,
                    kickbikeRounds: 100,
                    numRentMonths: 0
                },
                costs: {
                    writeOffs: 74069,
                    interest: 51124,
                    insurance: 12852,
                    trafficInsuranceFee: 3176,
                    maintenance: 6723,
                    fuel: 3358,
                    oil: 0,
                    tires: 6488,
                    serviceRepairs: 5430,
                    tolls: 9610
                },
                total: 172828.30,
                video: '/videos/bil_6.mp4',
                fallback: '/videos/bil_6.png',
                idleTime: 4
            }
        ],
        labels: {
            writeOffs: 'Avskrivninger',
            interest: 'Renter av bundet kapital',
            insurance: 'Forsikring',
            trafficInsuranceFee: 'Trafikkforsikringsavgift',
            maintenance: 'Vedlikehold',
            fuel: 'Drivstoff',
            oil: 'Olje',
            tires: 'Dekk',
            serviceRepairs: 'Service og reparasjoner',
            tolls: 'Bompenger'
        }
    },
    actions: {

    },
    mutations: {
        setSelectedPath (path) {
            state.selectedPath = path;
        },
        setSelectedIndex (state, index) {
            state.selectedIndex = index;
        }
    },
    getters: {
        activeSelection (state) {
            return state.selections[state.selectedIndex];
        },
        activeComparisons (state) {
            return state.comparisons.map(c => Object.assign({}, c, {
                value: state.selections[state.selectedIndex].compareValues[c.id],
                priceInfo: state.priceInfo[c.id],
                annualPrice: state.priceInfo[c.id].price * state.selections[state.selectedIndex].compareValues[c.id],
            }));        },
        activeDetails (state) {
            return Object.keys(state.labels)
                .map(k => ({label: state.labels[k], value: state.selections[state.selectedIndex].costs[k]}));
        }
    }
});
